import moment from 'moment'

import { formatService, FORMAT_TYPES as COMMON_FORMAT_TYPES } from '../common/format.service'

const MISSING_POINT_DISPLAY_OPTIONS = {
  PREVIOUS: 'previous',
  NEXT: 'next'
}

export const FORMAT_TYPES = {
  ...COMMON_FORMAT_TYPES,
  DATE: 'date'
}

class LineFormatService {
  /**
   * Format a single value for display in the line chart using the given configuration
   */
  formatValue (value, config) {
    const formatter = this.getFormatter(config)
    if (formatter) {
      return formatter(value)
    } else if (value === 0 || (value && value.toString)) {
      return value.toString()
    } else {
      return ''
    }
  }

  /**
   * Create a formatter function based on the given format type
   *
   * This is passed to the axes and other line chart components for properly displaying a line chart value.
   */
  getFormatter ({
    formatType,
    timeFormat = 'YYYY-MM-DD'
  }) {
    const formatDate = (value) => moment.utc(value).format(timeFormat)

    switch (formatType) {
      case FORMAT_TYPES.DATE:
        return formatDate
      case FORMAT_TYPES.NUMBER:
        return (value) => formatService.formatNumber(value)
      case FORMAT_TYPES.NUMBER_SHORT:
        return (value) => formatService.formatNumberShort(value)
      case FORMAT_TYPES.NUMBER_SHORTER:
        return (value) => formatService.formatNumberShorter(value)
      case FORMAT_TYPES.PERCENT:
        return (value) => formatService.formatPercent(value)
      case FORMAT_TYPES.PERCENT_SHORT:
        return (value) => formatService.formatPercentShort(value)
      case FORMAT_TYPES.PERCENT_SHORTER:
        return (value) => formatService.formatPercentShorter(value)
      default:
        return (value) => {
          value = value ?? ''
          if (value instanceof Date) {
            return formatDate(value)
          }
          return value.toString()
        }
    }
  }

  formatDataForTooltip ({ closestPoints, series, primaryValueFormat, secondaryValueFormat, ciFormat, seriesToHide = {}, missingPointDisplay }) {
    return closestPoints
      .map(({ current: closestPoint, previous, next }, i) => {
        if (!closestPoint) {
          // If missing, see if we want to use previous or next value instead
          switch (missingPointDisplay) {
            case MISSING_POINT_DISPLAY_OPTIONS.PREVIOUS:
              closestPoint = previous
              break
            case MISSING_POINT_DISPLAY_OPTIONS.NEXT:
              closestPoint = next
              break
          }
          // If still missing, no more work to do
          if (!closestPoint) {
            return {}
          }
        }

        const { id, name, label, color, bands } = series[i]
        const result = {
          id,
          label: label || name,
          color,
          primaryValue: this.formatValue(closestPoint.y, { formatType: primaryValueFormat }),
          secondaryValue: closestPoint.y2 && this.formatValue(closestPoint.y2, { formatType: secondaryValueFormat }),
          rawPrimaryValue: closestPoint.y,
          bands: bands?.map(({ name, points, color }) => {
            const point = points.find(({ x }) => x === closestPoint.x)
            return {
              label: name,
              color,
              value: `${this.formatValue(point.y0, { formatType: ciFormat })} - ${this.formatValue(point.y1, { formatType: ciFormat })}`
            }
          })?.reverse() ?? []
        }

        return result
      })
      .filter(({ label, rawPrimaryValue }) => ((rawPrimaryValue !== undefined) && (!seriesToHide[label])))
      .sort((a, b) => b.rawPrimaryValue - a.rawPrimaryValue)
  }
}

export const lineFormatService = new LineFormatService()
